<template lang="html">
  <div>
    <b-row>
      <b-col>
        <b-card class="shipping-info">
          <h4>
            <translate>Shipping Info</translate>
            <small><small>[<a :href="'/edit/shippings/'+ shipping.id" target="_blank">{{shipping.id}}</a>] <a href="#" @click.prevent="copyToClipboard(shipping.id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></small></small>
          </h4>
          <dl v-if="shipping.tour_id" class="mb-0">
            <dt v-translate>In Tour</dt>
            <dd><router-link :to="{ name: 'viewTour', params: { id: shipping.tour_id } }" target="_blank">{{shipping.tour_id}}</router-link> <a href="#" @click.prevent="copyToClipboard(shipping.tour_id, 'Tour ID')" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></dd>
          </dl>
          <b-row v-if="shipping.previous_shipping || shipping.following_shippings.length > 0">
            <b-col>
              <hr>
              <h4><translate>Roundtrip Shipping:</translate>
                <strong v-if="!shipping.previous_shipping" v-translate>Forth</strong>
                <span v-else>
                  <router-link :to="{name: 'viewShipping', params: {'id': shipping.previous_shipping.id}}" v-translate>Forth</router-link>
                  <small><small><a href="#" @click.prevent="copyToClipboard(shipping.previous_shipping.id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></small></small>
                </span>
                <font-awesome-icon icon="exchange-alt" size="1x" />
                <strong v-if="!shipping.following_shippings || shipping.following_shippings.length === 0" v-translate>Back</strong>
                <span v-else>
                  <router-link :to="{name: 'viewShipping', params: {'id': shipping.following_shippings[0].id}}" v-translate>Back</router-link>
                  <small><small><a href="#" @click.prevent="copyToClipboard(shipping.following_shippings[0].id)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></small></small>
                </span>
              </h4>
              <hr>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mb-2">
              <b-button size="sm" class="mb-1" @click.prevent v-b-modal.modalShippingMap v-translate>View Shipping Map</b-button>
              <b-button size="sm" class="mb-1" v-b-modal.modalCheckpointsLog v-translate>Checkpoints Log</b-button>
              <b-button size="sm" class="mb-1" @click.prevent="fetchForensicsLog(0)" v-translate>Forensics Log</b-button>
              <b-button :disabled="(!isAdmin || !isSuperAdmin) && !shipping.is_outsourced" size="sm" class="mb-1" @click.prevent="fetchAuditLog(1)" v-b-modal.modalAuditLog v-translate>3P Audit Log</b-button>
              <b-button size="sm" class="mb-1" v-if="hasShippingPlatformInformation" @click.prevent="fetchShippingPlatformInformation" v-translate>Platform Information</b-button>
              <b-button size="sm" class="mb-1" @click.prevent v-b-modal.modalDetails>
                <font-awesome-icon v-if="detailsShowing" :icon="['far', 'eye-slash']" size="2x" style="font-size: 16px;vertical-align: middle" />
                <font-awesome-icon v-else :icon="['far', 'eye']" size="2x" style="font-size: 16px;vertical-align: middle" />
              </b-button>
              <b-button size="sm" :disabled="!shipping.budget" class="mb-1" @click.prevent v-b-modal.modalShowQuoteUsed v-translate>
                View Quote Used
              </b-button>
              <b-button size="sm" class="mb-1" v-b-modal.modalEventsHistory v-translate>
                Shipping Events History
              </b-button>
              <br>
              <b-button size="sm" variant="warning" :disabled="!shipping.budget" class="mb-1" @click.prevent="recalculateBudget" v-if="(isSuperAdmin || isOperator) && isEditable" v-translate>
                Recalculate Budget
              </b-button>
              <b-button size="sm" variant="warning" class="mb-1" :href="transactionsLink" target="_blank" v-translate>
                Shipping Transactions
              </b-button>
            </b-col>
          </b-row>
          <b-row>
          <b-col md="6">
            <dl>
              <dt v-translate>Current Status</dt>
              <dd>
                {{shipping.status}}
                <b-button size="sm" variant="danger" @click.prevent="anonymizeShipping" v-if="shipping.status === 'DELIVERED' && shipping.to_contact && shipping.to_contact.indexOf('cleared_') === -1" v-translate>
                  Anonymize Shipping Data
                </b-button>
              </dd>

              <dt v-translate>Created</dt>
              <dd>{{moment.utc(shipping.created_at).tz($tz).format('L LT')}}</dd>

              <dt v-translate>Last Modified</dt>
              <dd>{{moment.utc(shipping.updated_at).tz($tz).format('L LT')}}</dd>

              <dt v-if="shipping.scheduled_date" v-translate>Scheduled Date</dt>
              <dd v-if="shipping.scheduled_date">
                {{moment.utc(shipping.scheduled_date).tz($tz).format('L LT')}}
                <span v-if="shipping.delivery_time_range">
                  <b-badge variant="dark" :title="shipping.delivery_time_range.time_from.slice(0, -3) + ' - ' + shipping.delivery_time_range.time_to.slice(0, -3)" v-b-tooltip.hover>{{shipping.delivery_time_range.name}}</b-badge>
                </span>
              </dd>

              <dt v-translate>Price</dt>
              <dd>
                <span v-show="editing !== 'price'">
                  {{shipping.price}} {{shipping.currency}}
                  <b-button v-if="(isEditable && isHeadOperations) || isAccountant" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'price'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                </span>
                <b-input-group size="sm" v-show="editing === 'price'">
                  <b-input type="number" v-model="shipping.price" @input="updatePatch(patchField, 'price', $event)" />
                  <b-input-group-append>
                    <b-input-group-text>{{shipping.currency}}</b-input-group-text>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
              </dd>

              <dt v-translate>Courier Commission</dt>
              <dd>
                <span v-show="editing !== 'courier_commission'">
                  {{shipping.courier_commission}} {{shipping.currency}}
                  <b-button v-if="(isEditable && isHeadOperations) || isAccountant" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'courier_commission'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                </span>
                <b-input-group size="sm" v-show="editing === 'courier_commission'">
                  <b-input type="number" v-model="shipping.courier_commission" @input="updatePatch(patchField, 'courier_commission', $event)" />
                  <b-input-group-append>
                    <b-input-group-text>{{shipping.currency}}</b-input-group-text>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
                <div v-if="shipping.events !== null && isEditable" v-translate="{eventsCommission: shipping.events.formatted_total_commission}">With ongoing events: <strong>%{eventsCommision}</strong></div>
              </dd>

              <dt v-if="shipping.platform_id" v-translate>Platform</dt>
              <dd v-if="shipping.platform_id">
                <span>
                  {{platforms.find(x => x.id === shipping.platform_id).name}}
                </span>
              </dd>

              <dt v-if="shipping.charge_amount" v-translate>Cash On Delivery</dt>
              <dd v-if="shipping.charge_amount">
                {{shipping.charge_amount}} {{shipping.currency}}
              </dd>

              <dt v-translate>Type</dt>
              <dd>
                <span v-show="editing !== 'shipping_type_id'">
                  {{shippingTypes.find(x => x.id === shipping.shipping_type_id) ? shippingTypes.find(x => x.id === shipping.shipping_type_id).name : ''}}
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'shipping_type_id'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                </span>
                <b-input-group size="sm" v-show="editing === 'shipping_type_id'">
                  <b-form-select v-model="shipping.shipping_type_id" :options="shippingTypes.map(type => { return {text: type.name, value: type.id}})" @input="updatePatch(patchField, 'shipping_type_id', $event)">
                  </b-form-select>
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
                <span v-if="shipping.shipping_type_id === shippingTypes.find(x => x.name === 'pickup').id || shipping.shipping_type_id === shippingTypes.find(x => x.name === 'return').id">
                  <b-button size="sm" v-b-modal.modalPickedupShippings @click="getPickedupShippings"><font-awesome-icon :icon="['far', 'eye']" size="2x" style="font-size: 16px;vertical-align: middle" /></b-button>
                </span>
              </dd>

              <dt v-if="shipping.size_id" v-translate>Size</dt>
              <dd>
                <span v-show="editing !== 'size_id'">
                  {{shippingSizes.find(x => x.id === shipping.size_id) ? shippingSizes.find(x => x.id === shipping.size_id).name : ''}}
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'size_id'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                </span>
                <b-input-group size="sm" v-show="editing === 'size_id'">
                  <b-form-select v-model="shipping.size_id" :options="shippingSizes.map(type => { return {text: type.name, value: type.id}})" @input="updatePatch(patchField, 'size_id', $event)">
                  </b-form-select>
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
              </dd>

              <dt v-if="shipping.packages_count" v-translate>Pieces</dt>
              <dd v-if="shipping.packages_count">
                {{shipping.packages_count}}
              </dd>

              <dt v-translate>Secret Code</dt>
              <dd>{{shipping.secret_code}}</dd>

              <dt v-if="shipping.external_code" v-translate>External Code</dt>
              <dd v-if="shipping.external_code">{{shipping.external_code}}</dd>
            </dl>
          </b-col>
          <b-col>
            <div v-if="shipping.user">
              <h5 v-translate>Created by User</h5>
              <user-card :user="shipping.user" :with-titles="false" :with-map="false" vertical />
            </div>
            <dl v-if="shipping.company">
              <dt v-translate>Created by Company</dt>
              <dd><a :href="'/edit/companies/'+ shipping.company.id" target="_blank">#{{shipping.company.id}}</a> {{shipping.company.name}}</dd>

              <dt v-translate>Company Contact</dt>
              <dd>{{shipping.company.contact_name}}</dd>

              <dt v-translate>Company Email</dt>
              <dd>{{shipping.company.email}}</dd>

              <dt v-translate>Company Phone</dt>
              <dd>{{shipping.company.phone}} <a class="btn btn-sm btn-secondary rounded-pill" style="color:#fff" :href="'tel:' + shipping.company.phone" v-translate>Call</a></dd>
            </dl>
            <div v-if="shipping.pickup_shipping_id">
              <hr>
              <dl class="mb-0">
                <dt v-translate>Picked Up in</dt>
                <dd><router-link :to="{ name: 'viewShipping', params: { id: shipping.pickup_shipping_id } }" target="_blank">{{shipping.pickup_shipping_id}}</router-link> <a href="#" @click.prevent="copyToClipboard(shipping.pickup_shipping_id, 'Pickup Shipping ID')" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a></dd>
              </dl>
            </div>
          </b-col>
        </b-row>
        <b-row v-if="tags.length > 0">
          <b-col>
            <h5 v-translate>Tags</h5>
            <div>
              <vue-tags-input
                v-model="tag"
                :tags="tags"
                :autocomplete-items="autocompleteItems"
                @tags-changed="$parent.getShipping()"
                @before-adding-tag="checkTag"
                @before-deleting-tag="deleteTag"
                :disabled="true"
                :placeholder="$gettext('Add Tag')"
              />
            </div>
          </b-col>
        </b-row>
        </b-card>
      </b-col>
      <b-col cols="12" :md="oneColumn === false ? 6 : 12">
        <div class="text-center mb-3" v-if="hasQr || hasDeliverySecurityCode || hasDeliveryAuthorizedReceiver">
          <b-button size="sm" variant="dark" v-b-modal.modalShowQr v-if="shipping.external_label">
            <font-awesome-icon icon="qrcode" /> <translate>ML FLEX QR</translate>
          </b-button>
          <b-button size="sm" variant="secondary" v-b-modal.modalShowQr v-else>
            <font-awesome-icon icon="qrcode" /> <translate>Pickup Type QR</translate>
          </b-button>
          <b-button size="sm" variant="danger" @click="clearSecurityOnDelivery('code')" v-if="hasDeliverySecurityCode" v-translate>
            Clear Delivery Security Code
          </b-button>
          <b-button size="sm" variant="danger" @click="clearSecurityOnDelivery('authorized')" v-if="hasDeliveryAuthorizedReceiver" v-translate>
            Clear Authorized Receivers
          </b-button>
        </div>
        <b-modal size="md" id="modalShowQr" ref="modalShowQr" hide-header hide-footer>
          <img v-if="shipping.external_label" :src="'https://api.qrserver.com/v1/create-qr-code/?size=420x420&ecc=L&data=' + encodeURIComponent(shipping.external_label.code ? JSON.stringify(JSON.parse(shipping.external_label.code)) : JSON.stringify(shipping.external_label))" alt="">
          <img v-else :src="'https://api.qrserver.com/v1/create-qr-code/?size=420x420&ecc=L&data=' + encodeURIComponent(shipping.id)" alt="">
        </b-modal>
        <b-card v-show="(shipping.status !== 'DELIVERED' && shipping.status !== 'CANCELED' && shipping.status !== 'RETURNED') && !shipping.is_outsourced">
          <h4 v-translate>Op Actions</h4>
          <b-form @submit.prevent v-if="(shipping.status === 'WAITING' || shipping.status === 'READY' || shipping.status === 'INCIDENCE')">
            <b-form-row v-if="statusesMap[shipping.status].length > 0">
              <div class="form-group col-sm-8" v-if="!courier || !courier.id || (!courier && !shipping.courier_id) || shipping.courier_id === courier.id">
                <label for="search-courier" v-translate>Search for courier by email</label>
                <user-autocomplete ref="courierAc" @selected="onSearchCourierSelected" :restrictToRoles="['courier']" with-picture />
              </div>
              <div class="form-group col-sm-8" v-if="courier && courier.id && shipping.courier_id !== courier.id">
                <label v-translate>Assign to courier</label>
                <user-card :user="courier" :with-titles="false" :with-map="false" />
              </div>
              <div class="form-group col-sm-4">
                <label for="from_extra">&nbsp;</label>
                <div class="input-group">
                  <b-btn size="sm" v-if="courier" @click="assignShipping(shipping.id)" :disabled="!courier.id || shipping.courier_id === courier.id" v-translate>Assign!</b-btn>
                  <b-btn variant="warning" size="sm" v-if="allowResetCourier && courier && courier.id && shipping.courier_id !== courier.id" @click="courier = {}" :disabled="!courier.id" class="mt-1" v-translate>Search again</b-btn>
                </div>
              </div>
            </b-form-row>
          </b-form>

          <b-form @submit.prevent="updateStatus(shipping.id)">
            <b-form-row v-if="statusesMap[shipping.status] && statusesMap[shipping.status].length > 0">
              <div class="form-group col-sm-8">
                <label for="change-status" v-translate>Change Shipping Status</label>
                <div class="input-group">
                  <b-form-select v-model="newShippingStatus" class="mb-2 mr-sm-2 mb-sm-0" required id="change-status">
                    <option :value="null" v-translate>Select an option</option>
                    <option :value="status" v-for="(status, index) in statusesMap[shipping.status]" :key="index">
                      {{ status }}
                    </option>
                  </b-form-select>
                </div>
                <div v-if="newShippingStatus === 'READY' || newShippingStatus === 'WAITING'">
                  <b-form-group class="mt-2">
                    <label for="scheduled-at" class="d-block" v-translate>Schedule Date (optional)</label>
                    <input type="date" v-model="reScheduledAt" class="form-control" style="width: auto; display: inline-block">
                    <b-form-select v-model="reScheduledAtHour" :options="timeSlots" style="width: auto; display: inline-block"></b-form-select>
                  </b-form-group>
                </div>
                <div v-if="newShippingStatus === 'CONFIRMED' && (!courier || !courier.id)">
                  <div class="invalid-feedback d-block" v-translate>
                    CONFIRMED status requires to search and select a courier above before submit the new status.
                  </div>
                </div>
                <div class="mb-3" v-if="newShippingStatus === 'CANCELED'">
                  <div class="invalid-feedback d-block" v-translate>
                    CANCELED status requires to select/write a reason below before submit the new status.
                  </div>
                  <b-form-group>
                    <label for="incidence-desc" v-translate>Cancel reason</label>
                    <b-form-radio-group v-model="cancelDetails" name="cancelDetails" required>
                      <b-form-radio v-for="(item, index) in shippingStatusReasons['CANCELED']" :value="{reason: item.name, reason_id: item.reason_id}" class="w-100" :key="index">
                        {{ item.description }}
                      </b-form-radio>
                      <b-form-radio :value="{reason: 'created_by_mistake'}" class="w-100"><translate>Created by mistake</translate></b-form-radio>
                      <b-form-radio :value="{reason: 'other'}" class="w-100"><translate>Other</translate></b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                  <b-form-group v-if="cancelDetails.reason === 'other'">
                    <label for="incidence-desc" v-translate>Incidence Description</label>
                    <div class="input-group">
                      <b-form-textarea
                        id="incidence-desc"
                        v-model="newShippingStatusMeta"
                        rows="3"
                        max-rows="6"
                        :state="validateIncidenceDesc"
                        required
                      />
                      <b-form-invalid-feedback :state="validateIncidenceDesc" v-translate>
                        "Other" CANCEL status requires a description. Please be brief but descriptive.
                      </b-form-invalid-feedback>
                      <b-form-valid-feedback :state="validateIncidenceDesc" v-translate>
                        Alright.
                      </b-form-valid-feedback>
                    </div>
                  </b-form-group>
                </div>
                <div v-if="newShippingStatus === 'INCIDENCE'">
                  <br>
                  <b-form-group>
                    <label for="incidence-desc" v-translate>Incidence type</label>
                    <b-form-radio-group v-model="incidenceDetails" name="incidenceDetails" required>
                      <b-form-radio v-for="(item, index) in shippingStatusReasons[shipping.status]" :value="{reason: item.name, reason_id: item.reason_id}" class="w-100" :key="index">
                        {{ item.description }}
                      </b-form-radio>
                      <b-form-radio :value="{reason: 'other'}" class="w-100"><translate>Other</translate></b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                  <b-form-group v-if="incidenceDetails.reason === 'other'">
                    <label for="incidence-desc" v-translate>Incidence Description</label>
                    <div class="input-group">
                      <b-form-textarea
                        id="incidence-desc"
                        v-model="newShippingStatusMeta"
                        rows="3"
                        max-rows="6"
                        :state="validateIncidenceDesc"
                        required
                      />
                      <b-form-invalid-feedback :state="validateIncidenceDesc" v-translate>
                        "Other" INCIDENCE status requires a description for the client. Please be brief but descriptive.
                      </b-form-invalid-feedback>
                      <b-form-valid-feedback :state="validateIncidenceDesc" v-translate>
                        Alright.
                      </b-form-valid-feedback>
                    </div>
                  </b-form-group>
                </div>
                <div v-if="newShippingStatus === 'TOBERETURNED'">
                  <br>
                  <b-form-group>
                    <label for="incidence-desc" v-translate>To Be Returned reason</label>
                    <b-form-radio-group v-model="tobereturnedDetails" name="tobereturnedDetails" required>
                      <b-form-radio :value="{reason: 'max_retries_reached'}" class="w-100"><translate>Maximum retries reached</translate></b-form-radio>
                      <b-form-radio v-for="(item, index) in tobereturnedStatusReasons" :value="{reason: item.name, reason_id: item.reason_id}" class="w-100" :key="index">
                        {{ item.description }}
                      </b-form-radio>
                      <b-form-radio :value="{reason: 'other'}" class="w-100"><translate>Other</translate></b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                  <b-form-group v-if="tobereturnedDetails.reason === 'other'">
                    <label for="incidence-desc" v-translate>To Be Returned Description</label>
                    <div class="input-group">
                      <b-form-textarea
                        id="incidence-desc"
                        v-model="newShippingStatusMeta"
                        rows="3"
                        max-rows="6"
                        :state="validateIncidenceDesc"
                        required
                      />
                      <b-form-invalid-feedback :state="validateIncidenceDesc" v-translate>
                        "Other" TOBERETURNED status requires a description for the client. Please be brief but descriptive.
                      </b-form-invalid-feedback>
                      <b-form-valid-feedback :state="validateIncidenceDesc" v-translate>
                        Alright.
                      </b-form-valid-feedback>
                    </div>
                  </b-form-group>
                </div>
                <div v-show="newShippingStatus === 'DELIVERED'">
                  <div v-if="isDeliverable">
                    <br>
                    <label for="receiver_type_id" v-translate>Receiver Type</label>
                    <div class="input-group">
                      <b-form-select v-model="deliveredStatusDetailFields.receiver_type_id" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_type_id">
                        <option :value="null" v-translate>Select an option</option>
                        <option :value="type.id" v-for="(type, index) in receiverTypes" :key="type.name + index">
                          {{ type.name }}
                        </option>
                      </b-form-select>
                    </div>
                    <br>
                    <label for="receiver_first_name" v-translate>Receiver First Name</label>
                    <div class="input-group">
                      <b-form-input v-model="deliveredStatusDetailFields.receiver_first_name" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_first_name" />
                    </div>
                    <br>
                    <label for="receiver_last_name" v-translate>Receiver Last Name</label>
                    <div class="input-group">
                      <b-form-input v-model="deliveredStatusDetailFields.receiver_last_name" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_last_name" />
                    </div>
                    <br>
                    <label for="receiver_address_street" v-translate>Address Street</label>
                    <div class="input-group">
                      <b-form-input v-model="deliveredStatusDetailFields.receiver_address_street" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_address_street" />
                    </div>
                    <br>
                    <label for="receiver_address_number" v-translate>Address Number</label>
                    <div class="input-group">
                      <b-form-input v-model="deliveredStatusDetailFields.receiver_address_number" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_address_number" />
                    </div>
                    <br>
                    <label for="receiver_address_extra" v-translate>Address Extra</label>
                    <div class="input-group">
                      <b-form-input v-model="deliveredStatusDetailFields.receiver_address_extra" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_address_extra" />
                    </div>
                    <br>
                    <label for="receiver_phone" v-translate>Receiver Phone</label>
                    <div class="input-group">
                      <b-form-input v-model="deliveredStatusDetailFields.receiver_phone" class="mb-2 mr-sm-2 mb-sm-0" id="receiver_phone" />
                    </div>
                  </div>
                  <div v-else>
                    <b-alert variant="warning" show class="mt-3">
                      <translate>The shipping is not deliverable because there are unmet settings conditions. (Ex.: items serial numbers not scanned.)</translate>
                    </b-alert>
                  </div>
                </div>
              </div>
              <div class="form-group col-sm-4">
                <label for="from_extra">&nbsp;</label>
                <div class="input-group">
                  <b-button size="sm" type="submit" :disabled="newShippingStatus === 'DELIVERED' && !isDeliverable" v-translate>Submit</b-button>
                </div>
              </div>
            </b-form-row>
          </b-form>
          <div v-if="isMoova && (isHeadOperations || isAdmin) && is3pable">
            <hr>
            <h5 v-translate>3P Outsource</h5>
            <b-button size="sm" @click="try3p" :disabled="outsourcing" variant="primary">
              <b-spinner small v-if="outsourcing" />
              <translate>Try to Outsource Shipping</translate>
            </b-button>
          </div>
        </b-card>
        <b-card v-if="shipping.is_outsourced" bg-variant="warning" text-variant="black">
          <h4 v-translate="{'3pCompany': shipping.courier_company?.name}">3P: %{3pCompany}</h4>
          <h5 v-translate="{'3pId': shipping.outsourced_id}" class="text-nowrap">3P ID: <strong>%{3pId}</strong></h5>
          <hr class="bg-black my-2">
          <company-card :company="shipping.courier_company" :profile-picture="false" priority-contact="operations" class="mb-2" />
          <b-button v-if="(isHeadOperations || isAdmin) && isEditable" @click="tryCancel3p" variant="danger" size="sm" v-translate>Cancel 3P</b-button>
        </b-card>
        <div v-if="shipping.threads.filter(x => x.type === 'internal').length > 0">
          <h4 v-translate>
            Internal Messages
          </h4>
          <div v-for="(thread, i) in shipping.threads.filter(x => x.type === 'internal')">
            <shipping-message-threads :the-thread="thread" :key="'thread' + thread.id" no-subject />
          </div>
        </div>
        <div class="text-center" v-else>
          <b-button size="sm" variant="dark" @click="messageThreadType = 'internal'" v-b-modal.modalCreateThread v-translate>
            Start a New Internal Message Thread
          </b-button>
        </div>
        <b-modal id="modalCreateThread" ref="modalCreateThread" size="lg" title="Submit your name" @hide="messageThreadType = ''" hide-footer>
          <div slot="modal-title" v-translate="{threadType: messageThreadType.toUpperCase()}">New %{threadType} Message Thread</div>
          <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
          <shipping-message-thread-form :type="messageThreadType" :shippingId="shipping.id" no-subject @success="$refs.modalCreateThread.hide(); $parent.getShipping();" @cancel="$refs.modalCreateThread.hide()" />
        </b-modal>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-card v-if="shipping.id" class="shipping-item" :class="shipping.status ? shipping.status.toLowerCase() : ''">
          <h4 v-translate>Points</h4>
          <b-row>
            <b-col>
              <h5>
                <translate>Description</translate>
                <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'description'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
              </h5>
              <p><span v-show="editing !== 'description'">
                {{shipping.description}}
              </span>
              <b-input-group size="sm" v-show="editing === 'description'">
                <b-input type="text" v-model="shipping.description" @input="updatePatch(patchField, 'description', $event)" />
                <b-input-group-append>
                  <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                  <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                </b-input-group-append>
              </b-input-group></p>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <h5>
                <translate>General instructions</translate>
                <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'instructions'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
              </h5>
              <p><span v-show="editing !== 'instructions'">
                {{shipping.instructions}}
              </span>
              <b-input-group size="sm" v-show="editing === 'instructions'">
                <b-input type="text" v-model="shipping.instructions" @input="updatePatch(patchField, 'instructions', $event)" />
                <b-input-group-append>
                  <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                  <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                </b-input-group-append>
              </b-input-group></p>
            </b-col>
          </b-row>
          <b-row>
            <b-col :cols="oneColumn === false ? 4 : 12" class="shipping-origin">
              <h5 class="shipping-contact" style="font-size:18px;line-height:1.4"><span v-show="editing !== 'from_contact'">
                {{shipping.from_contact}}
                <b-button pill v-if="isEditable" size="sm" variant="primary" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'from_contact'" :disabled="editing !== null"><font-awesome-icon class="text-white" icon="edit" /></b-button>
              </span>
              <b-input-group size="sm" v-show="editing === 'from_contact'">
                <b-input type="text" v-model="shipping.from_contact" @input="updatePatch(patchField, 'from_contact', $event)" />
                <b-input-group-append>
                  <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                  <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                </b-input-group-append>
              </b-input-group></h5>
              <p>
                <span v-if="(!shipping.fields_revision || shipping.fields_revision === false) || !shipping.fields_revision_data.from || typeof shipping.fields_revision_data.from === 'object'">
                  <span
                    class="shipping-address"
                    v-show="editing !== 'from_place_id'"
                    :class="shipping.fields_revision_data &&
                      shipping.fields_revision_data.from &&
                      typeof shipping.fields_revision_data.from === 'object' ? 'text-warning' : ''"
                    :title="shipping.address_from.formatted_address"><font-awesome-icon v-if="shipping.fields_revision_data &&
                      shipping.fields_revision_data.from &&
                      typeof shipping.fields_revision_data.from === 'object'" icon="exclamation-triangle" size="1x" />
                    <b-button pill size="sm" @click.stop="openMapOn(shipping.address_from.formatted_address + '/@' + shipping.address_from.lat + ',' + shipping.address_from.lng + ',17z')" style="padding: .05rem .25rem; vertical-align: bottom" :title="$gettext('Quick view address map on new window')">
                      <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px;vertical-align: middle" />
                    </b-button>
                    <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'from_place_id'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                    {{shipping.address_from.formatted_address}}
                  </span>
                  <b-input-group size="sm" v-show="editing === 'from_place_id'">
                    <address-autocomplete ref="acFromPlaceId" @selected="checkFromPlace" :minCharacter="4" />
                    <b-input-group class="mt-1">
                      <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                      <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                    </b-input-group>
                  </b-input-group>
                </span>
                <span v-if="shipping.fields_revision_data !== null && typeof shipping.fields_revision_data.from === 'string'">
                  <span
                    class="shipping-address text-danger"
                    :title="shipping.fields_revision_data.from"><font-awesome-icon icon="exclamation-triangle" size="1x" /> {{shipping.fields_revision_data.from}}</span>
                </span>
                <b-button v-if="shipping.fields_revision_data && shipping.fields_revision_data.from" size="sm" @click.prevent v-b-modal.modalFixAddressFrom v-translate>Review Address</b-button>
                <br>
                <span v-show="editing !== 'from_extra'">
                  <span v-translate="{data: shipping.from_extra || '-'}">Floor, door, etc.: %{data}</span>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'from_extra'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                  <br>
                </span>
                <b-input-group class="my-1" size="sm" prepend="Floor, door, etc.: " v-show="editing === 'from_extra'">
                  <b-input type="text" v-model="shipping.from_extra" @input="updatePatch(patchField, 'from_extra', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
                <span v-show="editing !== 'from_email'">
                  <span v-translate="{data: shipping.from_email || '-'}">Email: %{data}</span>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'from_email'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                  <br>
                </span>
                <b-input-group class="my-1" size="sm" prepend="Email: " v-show="editing === 'from_email'">
                  <b-input type="email" v-model="shipping.from_email" @input="updatePatch(patchField, 'from_email', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
                <span v-show="editing !== 'from_phone'">
                  <span v-translate="{data: shipping.from_phone || '-'}">Phone: %{data}</span>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'from_phone'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                  <a class="btn btn-sm btn-secondary rounded-pill" style="color:#fff" :href="'tel:' + shipping.from_phone" v-translate>Call</a>&nbsp;<b-button pill size="sm" style="color:#fff" v-translate>Send SMS(?)</b-button>
                </span>
                <b-input-group class="my-1" size="sm" prepend="phone: " v-show="editing === 'from_phone'">
                  <b-input type="email" v-model="shipping.from_phone" @input="updatePatch(patchField, 'from_phone', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
              </p>
              <div>
                <h6>
                  <translate>Instructions on origin</translate>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); shipping.from_meta = shipping.from_meta ? shipping.from_meta.split('---')[0] : ''; editing = 'from_meta'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                </h6>
                <p><span v-show="editing !== 'from_meta'">
                  <span v-html="$options.filters.brize(shipping.from_meta)"></span>
                </span>
                <b-input-group size="sm" v-show="editing === 'from_meta'">
                  <b-input type="text" v-model="shipping.from_meta" @input="updatePatch(patchField, 'from_meta', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group></p>
              </div>
            </b-col>
            <b-col :cols="oneColumn === false ? 4 : 12" class="shipping-midpoint">
              <h5 class="shipping-contact" style="font-size:18px; line-height:1.4" v-translate>Last Known Checkpoint</h5>
              <p><span>
                <span class="shipping-address">
                  <b-button pill size="sm" @click.stop="openMapOn(shipping.last_known_place.formatted_address + '/@' + shipping.last_known_place.lat + ',' + shipping.last_known_place.lng + ',17z')" style="padding: .05rem .25rem; vertical-align: bottom" :title="$gettext('Quick view address map on new window')">
                    <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px;vertical-align: middle" />
                  </b-button>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'last_known_place'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                    {{shipping.last_known_place.formatted_address}}
                  </span>
                  <b-input-group size="sm" v-show="editing === 'last_known_place'">
                    <address-autocomplete ref="acLastKnownPlaceId" @selected="checkLastKnownPlace" :minCharacter="4" />
                    <b-input-group class="mt-1">
                      <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                      <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                    </b-input-group>
                  </b-input-group>
              </span></p>
            </b-col>
            <b-col :cols="oneColumn === false ? 4 : 12" class="shipping-destination">
              <h5 class="shipping-contact" style="font-size:18px;line-height:1.4"><span v-show="editing !== 'to_contact'">
                {{shipping.to_contact}}
                <b-button pill v-if="isEditable" size="sm" variant="primary" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'to_contact'" :disabled="editing !== null"><font-awesome-icon class="text-white" icon="edit" /></b-button>
              </span>
              <b-input-group size="sm" v-show="editing === 'to_contact'">
                <b-input type="text" v-model="shipping.to_contact" @input="updatePatch(patchField, 'to_contact', $event)" />
                <b-input-group-append>
                  <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                  <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                </b-input-group-append>
              </b-input-group></h5>
              <p>
                <span v-if="(!shipping.fields_revision || shipping.fields_revision === false) || !shipping.fields_revision_data.to || typeof shipping.fields_revision_data.to === 'object'">
                  <span
                    class="shipping-address"
                    v-show="editing !== 'to_place_id'"
                    :class="shipping.fields_revision_data &&
                      shipping.fields_revision_data.to &&
                      typeof shipping.fields_revision_data.to === 'object' ? 'text-warning' : ''"
                    :title="shipping.address_to.formatted_address"><font-awesome-icon v-if="shipping.fields_revision_data &&
                      shipping.fields_revision_data.to &&
                      typeof shipping.fields_revision_data.to === 'object'" icon="exclamation-triangle" size="1x" />
                    <b-button pill size="sm" @click.stop="openMapOn(shipping.address_to.formatted_address + '/@' + shipping.address_to.lat + ',' + shipping.address_to.lng + ',17z')" style="padding: .05rem .25rem; vertical-align: bottom" :title="$gettext('Quick view address map on new window')">
                      <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px;vertical-align: middle" />
                    </b-button>
                    <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'to_place_id'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                    {{shipping.address_to.formatted_address}}
                  </span>
                  <b-input-group size="sm" v-show="editing === 'to_place_id'">
                    <address-autocomplete ref="acToPlaceId" @selected="checkToPlace" :minCharacter="4" />
                    <b-input-group class="mt-1">
                      <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                      <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                    </b-input-group>
                  </b-input-group>
                </span>
                <span v-if="shipping.fields_revision_data !== null && typeof shipping.fields_revision_data.to === 'string'">
                  <span class="shipping-address text-danger" :title="shipping.fields_revision_data.to"><font-awesome-icon icon="exclamation-triangle" size="1x" /> {{shipping.fields_revision_data.to}}</span>
                </span>
                <b-button v-if="shipping.fields_revision_data && shipping.fields_revision_data.to" size="sm" @click.prevent v-b-modal.modalFixAddressTo>Review Address</b-button>
                <br>
                <span v-show="editing !== 'to_extra'">
                  <span v-translate="{data: shipping.to_extra || '-'}">Floor, door, etc.: %{data}</span>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'to_extra'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                  <br>
                </span>
                <b-input-group class="my-1" size="sm" prepend="Floor, door, etc.: " v-show="editing === 'to_extra'">
                  <b-input type="text" v-model="shipping.to_extra" @input="updatePatch(patchField, 'to_extra', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
                <span v-show="editing !== 'to_email'">
                  <span v-translate="{data: shipping.to_email || '-'}">Email: %{data}</span>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'to_email'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                  <br>
                </span>
                <b-input-group class="my-1" size="sm" prepend="Email: " v-show="editing === 'to_email'">
                  <b-input type="email" v-model="shipping.to_email" @input="updatePatch(patchField, 'to_email', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
                <span v-show="editing !== 'to_phone'">
                  <span v-translate="{data: shipping.to_phone || '-'}">Phone: %{data}</span>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); editing = 'to_phone'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                  <a class="btn btn-sm btn-secondary rounded-pill" style="color:#fff" :href="'tel:' + shipping.to_phone" v-translate>Call</a>&nbsp;<b-button pill size="sm" style="color:#fff" v-translate>Send SMS(?)</b-button>
                </span>
                <b-input-group class="my-1" size="sm" prepend="phone: " v-show="editing === 'to_phone'">
                  <b-input type="email" v-model="shipping.to_phone" @input="updatePatch(patchField, 'to_phone', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group>
              </p>
              <div>
                <h6>
                  <translate>Instructions on destination</translate>
                  <b-button v-if="isEditable" size="sm" variant="link" style="padding:0 .5rem" @click.prevent="Object.assign(originalShipping, shipping); shipping.to_meta = shipping.to_meta ? shipping.to_meta.split('---')[0] : ''; editing = 'to_meta'" :disabled="editing !== null"><font-awesome-icon icon="edit" /></b-button>
                </h6>
                <p><span v-show="editing !== 'to_meta'">
                  <span v-html="$options.filters.brize(shipping.to_meta)"></span>
                </span>
                <b-input-group size="sm" v-show="editing === 'to_meta'">
                  <b-input type="text" v-model="shipping.to_meta" @input="updatePatch(patchField, 'to_meta', $event)" />
                  <b-input-group-append>
                    <b-button size="sm" variant="success" @click.prevent="patchShipping"><font-awesome-icon icon="check" size="sm" /></b-button>
                    <b-button size="sm" variant="danger" @click.prevent="Object.assign(shipping, originalShipping); patchField = {}; editing = null">×</b-button>
                  </b-input-group-append>
                </b-input-group></p>
              </div>
            </b-col>
          </b-row>
        </b-card>
        <b-card v-if="shipping.itemsToShow.length > 0">
          <h4 v-translate>Shipping Items</h4>
          <b-table
            :items="shipping.itemsToShow"
            :fields="['description', 'quantity', 'serial_number']"
          >
            <template v-slot:cell(description)="row">
              - {{row.item.description || $gettext('No description')}}
            </template>
            <template v-slot:cell(quantity)="row">
              {{row.item.quantity || 1}} u.
            </template>
            <template v-slot:cell(serial_number)="row">
              <span v-if="row.item.original_serial_number && row.item.original_serial_number.length > 0">
                {{row.item.original_serial_number.join(', ')}}
              </span>
              <b-button size="sm" :disabled="shipping.status !== 'DELIVERED' && 1 === 0" variant="link" style="padding:0 .5rem" @click.prevent="itemEdit = row.item; editing = 'item'" v-if="isSuperAdmin || isOperator" v-b-modal.modalItemEdit><font-awesome-icon icon="edit" /></b-button>
            </template>
          </b-table>

          <b-modal id="modalItemEdit" ref="modalItemEdit" hide-footer @hide="itemEdit = {}; editing = null; $emit('shippingupdated', shipping)">
            <div slot="modal-title" v-translate>Edit Serial Numbers</div>
            <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
            <form @submit.stop.prevent="patchItem">
              <div class="form-group col-sm-12">
                <div v-for="(serialNumber, i) in itemEdit.quantity" :key="i" class="mb-3">
                  <label for="" v-translate="{data: itemEdit.original_serial_number[i] || '-'}">Original SN: %{data}</label>
                  <b-form-input v-model="itemEdit.serial_number[i]"
                    type="text"
                  ></b-form-input>
                </div>
              </div>
              <div class="form-group col-sm-12 mt-3">
                <b-button variant="primary" type="submit" v-translate>
                  Send
                </b-button>
                <b-button variant="secondary" @click.prevent="$refs.modalItemEdit.hide()" v-translate>
                  Cancel
                </b-button>
              </div>
            </form>
          </b-modal>
        </b-card>
      </b-col>
      </b-row>
      <b-row>
        <b-col v-if="shipping.status === 'DELIVERED'">
          <b-card class="mt-3" :show="shipping.receiver_first_name ? true : false">
            <h4 v-translate>Delivered to Info</h4>
            <b-row>
              <b-col cols="3" v-if="(shipping.photos.length > 0 || shipping.receiver_signature) && (shipping.receiver_signature || (shipping.photos.find(x => x.type === 'SIGNATURE') && shipping.photos.find(x => x.type === 'SIGNATURE').full_path))">
                <a class="mr-2" href="#" v-b-modal.modalShowPicture @click.prevent="currentPicture = shipping.receiver_signature || (shipping.photos.find(x => x.type === 'SIGNATURE') && shipping.photos.find(x => x.type === 'SIGNATURE').full_path)"><img :src="shipping.receiver_signature || shipping.photos.find(x => x.type === 'SIGNATURE').full_path" alt="" style="height:100px; width: auto"></a>
              </b-col>
              <b-col>
              <ul>
                <li v-show="shipping.receiver_first_name"><translate>Receiver</translate>: {{shipping.receiver_first_name}} {{shipping.receiver_last_name}}</li>
                <li v-show="shipping.receiver_address_street"><translate>Address</translate>: {{shipping.receiver_address_street}} {{shipping.receiver_address_number}}{{shipping.receiver_address_extra ? ', ' + shipping.receiver_address_extra : ''}}</li>
                <li v-show="shipping.receiver_phone"><translate>Phone</translate>: {{shipping.receiver_phone}}</li>
                <li v-show="shipping.receiver_comments"><translate>Comments</translate>: {{shipping.receiver_comments}}</li>
              </ul>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col v-if="shipping.photos && shipping.photos.length > 0 && shipping.photos[0].type !== 'SIGNATURE'">
          <b-card class="mt-3">
            <h4 v-translate>Shipping Images</h4>
            <b-row class="mb-3" v-if="shipping.photos.length > 0">
              <b-col class="text-center" v-for="(photo, index) in shipping.photos" :key="index" v-if="shipping.photos[index].type !== 'SIGNATURE'">
                <a href="#" v-b-modal.modalShowPicture @click.prevent="currentPicture = photo.full_path"><img :src="photo.full_path" alt="" style="height:150px; width:auto"></a><br>{{statusStrings[photo.type]}}
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
      <b-col :cols="oneColumn === false ? 6 : 12" v-if="shipping.courier && shipping.courier !== null">
        <b-card class="current-courier">
          <h4 v-translate>Current Courier</h4>
          <user-card :user='shipping.courier' profile-picture-size="100px" />
        </b-card>
      </b-col>

      <b-col :cols="oneColumn === false ? 6 : 12" v-if="shipping.status_history && shipping.status_history.length > 0">
        <b-card class="shipping-statuses">
          <h4 v-translate>Status History</h4>
          <shipping-status-history :shipping="shipping" @updated="refreshShipping" />
        </b-card>
      </b-col>

    </b-row>
    <b-modal id="modalShowPicture" ref="modalShowPicture" size="lg" :hide-footer="true" @hidden="currentPicture = ''">
      <div slot="modal-title" v-translate>View picture</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <div class="w-100">
        <img :src="currentPicture" alt="" class="w-100">
      </div>
    </b-modal>

    <b-modal id="modalFixAddressFrom" ref="modalFixAddressFrom" :hide-footer="true">
      <div slot="modal-title" v-translate>Review FROM Address</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <fix-addresses-form :theShipping="shipping" type="from" @success="addressFixed('modalFixAddressFrom')" @cancel="closeModal('modalFixAddressFrom')" />
    </b-modal>
    <b-modal id="modalFixAddressTo" ref="modalFixAddressTo" :hide-footer="true">
      <div slot="modal-title" v-translate>Review TO Address</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <fix-addresses-form :theShipping="shipping" type="to" @success="addressFixed('modalFixAddressTo')" @cancel="closeModal('modalFixAddressTo')" />
    </b-modal>

    <b-modal id="modalShippingMap" size="xl" ref="modalShippingMap" :hide-header="true" :hide-footer="true" @hide="killMap">
      <leaflet-map ref="fullMap" style="height:80vh; position:relative;" :zoom="12" :fit-once="true" :geolocate="true" @ready="initMap" />
      <div v-if="shipping.courier_id">
        <b-form-checkbox v-model="showTracking" v-if="isSuperAdmin || isOperatorSupervisor || isOperator || isAccountant">
          <b-spinner small v-if="trackingWorking"></b-spinner>
          <translate>Show courier tracking</translate>
          <b-button v-if="trackingCsv.length > 0" size="sm" @click="downloadTrackingCsv" :disabled="trackingWorking"><font-awesome-icon icon="download" /></b-button>
        </b-form-checkbox>
        <b-form-checkbox v-if="showTracking" v-model="showHeathPath" :disabled="trackingWorking">
          <translate>Show courier heathPath</translate>
        </b-form-checkbox>

      </div>
    </b-modal>

    <b-modal id="modalDetails" size="lg" ref="modalDetails" :hide-header="true" :hide-footer="true" @show="detailsShowing = true" @hide="detailsShowing = false">
      <b-card>
        <ul>
          <li v-for="(value, key) in shipping" :key="key" v-if="value && value.length !== 0 && value !== 'undefined' && value !== null">{{ key }}: <span v-html="key.indexOf('phone') === -1 ? isJson(value) : value.toString()"></span></li>
        </ul>
      </b-card>
    </b-modal>

    <b-modal id="modalShippingPlatformInformation" size="lg" ref="modalShippingPlatformInformation" :hide-header="true" :hide-footer="true">
      <b-card>
        <ul>
          <li v-for="(value, key) in shippingPlatformInformation" :key="key" v-if="value && value.length !== 0 && value !== 'undefined' && value !== null">{{ key }}: <span v-html="key.indexOf('phone') === -1 ? isJson(value) : value.toString()"></span></li>
          </ul>
      </b-card>
    </b-modal>

    <b-modal id="modalShowQuoteUsed" ref="modalShowQuoteUsed" size="xl">
      <ul v-if="shipping.budget">
        <li v-for="(value, key) in shipping.budget" :key="key" v-if="value && value.length !== 0 && value !== 'undefined' && value !== null">{{ key }}: <span v-html="key.indexOf('phone') === -1 ? isJson(value) : value.toString()"></span></li>
      </ul>
    </b-modal>

    <b-modal size="xl" id="modalEventsHistory" ref="modalEventsHistory" hide-footer @show="getEventsHistory" @hide="eventsHistory = []">
      <div slot="modal-title" v-translate>Shipping Events History</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <b-table
       :items="eventsHistory"
       :fields="eventsHistoryFields"
       show-empty
       :empty-text="$gettext('There are no results to show')"
      >
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(event)="row">
          {{snakeCaseToTitleCase(row.value)}}
        </template>
        <template v-slot:cell(applied_by)="row">
          <user-card :user="row.value" :profile-picture="false" :with-titles="false" />
        </template>
        <template v-slot:cell(data)="row">
          <ul>
            <li v-for="(value, key) in row.value" :key="key" v-if="value && value.length !== 0 && value !== 'undefined' && value !== null">{{ key }}: <span v-html="key.indexOf('phone') === -1 ? isJson(value) : value.toString()"></span></li>
          </ul>
        </template>
        <template v-slot:cell(created_at)="row">
          {{moment.utc(row.value).tz($tz).format('L LT')}}
        </template>
        <template v-slot:cell(actions)="row">
          <b-button
            pill
            v-b-modal.modalStaticTrackingMap
            style="padding: .33rem .5rem;"
            size="sm"
            @click.stop="staticTrackingCoords = [{lat: row.item.lat, lng: row.item.lng}]"
          >
            <font-awesome-icon icon="map-marker-alt" size="2x" style="font-size: 18px;vertical-align: middle" />
          </b-button>
        </template>
      </b-table>
    </b-modal>

    <b-modal size="lg" id="modalStaticTrackingMap" ref="modalStaticTrackingMap" hide-footer hide-header @hide="staticTrackingCoords = []">
      <mapbox-static-map :markers="staticTrackingCoords" :size="[700, 700]"></mapbox-static-map>
    </b-modal>

    <b-modal id="modalForensicsLog" size="xl" ref="modalForensicsLog" hide-header hide-footer>
      <div slot="modal-title" v-translate>Shipping Changes Log</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <log-list :log="forensicsLog" @page-changed="fetchForensicsLog($event)" />
    </b-modal>

    <b-modal id="modalAuditLog" ref="modalAuditLog" size="lg" hide-footer @hidden="auditLog = {}">
      <div slot="modal-title" v-translate>3P Audit Log</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <log-list :log="auditLog" @page-changed="fetchAuditLog($event)" />
    </b-modal>

    <b-modal id="modalCheckpointsLog" ref="modalCheckpointsLog" size="lg" hide-footer>
      <div slot="modal-title" v-translate>Checkpoints Log</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <log-list :log="checkpointsList" />
    </b-modal>

    <b-modal size="lg" id="modalFromPickPlace" ref="modalFromPickPlace" hide-footer hide-header @hide="$refs.pickFromPlaceMap.pickPlace(0, 0)">
      <place-picker ref="pickFromPlaceMap" hasReference @cancel="closeModalFromPickPlace" @placePicked="setPlacePickedFrom" centerMap fit-always :zoom="17" />
    </b-modal>
    <b-modal size="lg" id="modalLastKnownPickPlace" ref="modalLastKnownPickPlace" hide-footer hide-header @hide="$refs.pickLastKnownPlaceMap.pickPlace(0, 0)">
      <place-picker ref="pickLastKnownPlaceMap" :hasReference="false" @cancel="closeModalLastKnownPickPlace" @placePicked="setPlacePickedLastKnown" centerMap fit-always :zoom="17" />
    </b-modal>
    <b-modal size="lg" id="modalToPickPlace" ref="modalToPickPlace" hide-footer hide-header @hide="$refs.pickToPlaceMap.pickPlace(0, 0)">
      <place-picker ref="pickToPlaceMap" hasReference @cancel="closeModalToPickPlace" @placePicked="setPlacePickedTo" centerMap fit-always :zoom="17" />
    </b-modal>

    <b-modal size="lg" id="modalPickedupShippings" ref="modalPickedupShippings" hide-footer @hide="pickedupShippings = []; pickedupShippingsToCopy = []">
      <div slot="modal-title" v-translate>Picked Up Shippings</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>

      <b-row>
        <b-col class="bg-white py-1">
          <span v-translate="{qty: pickedupShippings.length}">Total items: <strong>%{qty}</strong></span>
          <a href="#" @click.prevent="copyToClipboard(pickedupShippings.map(x => { return x.id }).join('\n'), 'Picked Up IDs')" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
        </b-col>
        <b-col class="text-right mb-3">
          <b-button size="sm" @click="printManifest" v-if="pickedupShippings.length > 0"><font-awesome-icon icon="print" /></b-button>
          <b-button size="sm" variant="primary" target="_blank" :disabled="pickedupShippings.length === 0" :href="'/list/shippings/manual?qs=%7B%22statuses%22%3A%5B%5D,%22types%22%3A%5B%5D,%22scheduleds%22%3A%5B%5D,%22scheduledsToday%22%3A%5B%5D,%22cutTime%22%3A%5B%5D,%22shippingId%22%3A%5B%5D,%22destinationContactName%22%3A%5B%5D,%22destinationContactEmail%22%3A%5B%5D,%22originAddress%22%3A%5B%5D,%22nearOrigin%22%3A%5B%5D,%22destinationAddress%22%3A%5B%5D,%22externalCode%22%3A%5B%5D,%22pickupId%22%3A%5B%22pickup_shipping_id%22,%22eq%22,%22' + shipping.id + '%22%5D,%22tag%22%3A%5B%5D,%22withoutTour%22%3A%5B%5D,%22company%22%3A%5B%5D,%22courier%22%3A%5B%5D,%22client%22%3A%5B%5D,%22forReview%22%3A%5B%5D,%22readyWithIncidence%22%3A%5B%5D,%22needUrgentAction%22%3A%5B%5D,%22createdAtFrom%22%3A%5B%5D,%22createdAtTo%22%3A%5B%5D,%22updatedAtFrom%22%3A%5B%5D,%22updatedAtTo%22%3A%5B%5D%7D'" v-translate>
            Open in Shipping List
          </b-button>
        </b-col>
      </b-row>
      <b-table striped
        :items="pickedupShippings"
        :fields="pickedupShippingsFields"
        :empty-text="$gettext('There are no picked up shippings to show yet')"
        show-empty
        :busy.sync="fetching"
      >
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(id)="data">
          <a :href="'/edit/shippings/' + data.value" target="_blank">{{data.value | uuid }}</a> <a href="#" @click.prevent="copyToClipboard(data.value)" class="text-secondary" :title="$gettext('Copy to Clipboard')"><font-awesome-icon :icon="['far', 'copy']" size="1x" /></a>
          <div v-if="data.item.platform_id">
            <span class="badge" :style="'background-color: ' + stringToColour(' ' + data.item.platform_id + data.item.platform_id + data.item.platform_id + data.item.platform_id) + ';' + textContrastColor(stringToColour(' ' + data.item.platform_id + data.item.platform_id + data.item.platform_id + data.item.platform_id))">{{platforms.find(x => x.id === data.item.platform_id).name}}</span>
          </div>
        </template>
        <template v-slot:cell(company_id)="row">
          <div v-if="row.item.company_id">
            <b-badge variant="dark" v-translate>Company</b-badge><br>
            <a :href="'/edit/companies/' + row.item.company_id" target="_blank">#{{row.item.company_id}}</a> {{row.item.company.name}}
          </div>
          <div v-else>
            <b-badge variant="secondary" v-translate>Particular</b-badge><br>
            <a :href="'/edit/users/' + row.item.user_id" target="_blank">#{{row.item.user_id}}</a> {{row.item.user.name}}
          </div>
        </template>
        <template v-slot:cell(size_id)="row">
          {{shippingSizes.find(x => x.id === row.item.size_id) ? shippingSizes.find(x => x.id === row.item.size_id).name : ''}}
        </template>
        <template v-slot:cell(shipping_type_id)="row">
          {{shippingTypes.find(x => x.id === row.item.shipping_type_id) ? shippingTypes.find(x => x.id === row.item.shipping_type_id).name : ''}}
        </template>
      </b-table>

      <div id="pickedup-manifest" class="d-none">
        <h4 v-translate="{shippingId: shipping.id}" class="text-center mb-3">Picked Up Shippings on %{shippingId}</h4>
        <div class="float-left mb-3" v-translate="{companyName: shipping.company.name}">Company: <strong>%{companyName}</strong></div>
        <div class="float-right text-right mb-3" v-translate="{count: pickedupShippings.length}">Shipping Qty.: <strong>%{count}</strong></div>
        <b-table striped
          :items="pickedupShippings"
          :fields="[
            {
              key: 'id',
              label: this.$gettextInterpolate(this.$gettext('%{siteName} ID'), {siteName: this.universe.siteName})
            },
            {
              key: 'external_code', label: this.$gettext('Code')
            },
            {
              key: 'shipping_type_id', label: this.$gettext('Type')
            },
            {
              key: 'status_history', label: this.$gettext('Picked Up At')
            },
            {
              key: 'from_contact', label: this.$gettext('Contact')
            }
          ]"
          empty-text="There are no picked up shippings to show yet"
          show-empty
          v-if="pickedupShippings.length > 0"
        >
        <template v-slot:cell(external_code)="row">
          <div v-if="row.item.external_code" v-translate="{externalCode: row.item.external_code}">External: %{externalCode}</div>
          <div v-if="row.item.external_order_id" v-translate="{externalOrderId: row.item.external_order_id}">Order: %{externalOrderId}</div>
        </template>
          <template v-slot:cell(status_history)="row">
            <div v-if="shippingTypes.find(x => x.id === row.item.shipping_type_id).name === 'reverse'" v-html="$options.filters.brize(getFirstIntransitDate(row.value))"></div>
          </template>
          <template v-slot:cell(from_contact)="row">
            <div v-if="shippingTypes.find(x => x.id === row.item.shipping_type_id).name === 'reverse'">{{row.value}}<br>{{row.item.from_phone}}</div>
          </template>
          <template v-slot:cell(shipping_type_id)="row">
            {{shippingTypes.find(x => x.id === row.item.shipping_type_id) ? shippingTypes.find(x => x.id === row.item.shipping_type_id).name : ''}}
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import AddressMixins from '@/mixins/address.mixin.js'
import ShippingsMixins from '@/mixins/shippings.mixin.js'
import UserAutocomplete from '@/components/autocompletes/UserAutocomplete'
import AddressAutocomplete from '@/components/autocompletes/AddressAutocomplete'
import UserCard from '@/components/UserCard'
import PlacePicker from '@/components/PlacePicker'
import VueTagsInput from '@johmun/vue-tags-input'
import LeafletMap from '@/components/LeafletMap'
import ShippingMessageThreads from '@/components/ShippingMessageThreads'
import ShippingMessageThreadForm from '@/components/ShippingMessageThreadForm'
import FixAddressesForm from '@/components/FixAddressesForm'
import MapboxStaticMap from '@/components/MapboxStaticMap'
import ShippingStatusHistory from '@/components/ShippingStatusHistory'
import LogList from '@/components/LogList'

export default {
  components: {
    UserAutocomplete,
    AddressAutocomplete,
    UserCard,
    PlacePicker,
    VueTagsInput,
    LeafletMap,
    ShippingMessageThreads,
    ShippingMessageThreadForm,
    FixAddressesForm,
    MapboxStaticMap,
    ShippingStatusHistory,
    LogList
  },
  props: {
    oneColumn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      shipping: {},
      originalShipping: {},
      courier: {},
      statusesMap: {
        DRAFT: ['DRAFT', 'READYATSTORE', 'READY', 'CANCELED', 'INCIDENCE'],
        READYATSTORE: ['DRAFT', 'READYATSTORE', 'READY', 'CANCELED'],
        READY: ['DRAFT', 'READY', 'CANCELED', 'WAITING', 'CONFIRMED', 'INCIDENCE'],
        WAITING: ['DRAFT', 'READY', 'WAITING', 'CANCELED', 'CONFIRMED'],
        BLOCKED: ['READY'],
        CONFIRMED: ['READY', 'CANCELED', 'ATPICKUPPOINT', 'INCIDENCE', 'PICKEDUP', 'INTRANSIT'],
        ATPICKUPPOINT: ['ATPICKUPPOINT', 'INCIDENCE', 'PICKEDUP', 'INTRANSIT', 'CANCELED'],
        PICKEDUP: ['INTRANSIT', 'CANCELED'],
        INTRANSIT: ['DELIVERED', 'INCIDENCE', 'CANCELED'],
        INCIDENCE: ['READY', 'WAITING', 'CONFIRMED', 'INTRANSIT', 'DELIVERED', 'TOBERETURNED', 'CANCELED'],
        TOBERETURNED: ['READY', 'RETURNED', 'CANCELED'],
        DELIVERED: [],
        CANCELED: [],
        RETURNED: []
      },
      newShippingStatus: null,
      newShippingStatusMeta: null,
      deliveredStatusDetailFields: {
        receiver_type_id: null,
        receiver_first_name: null,
        receiver_last_name: null,
        receiver_address_street: null,
        receiver_address_number: null,
        receiver_address_extra: null,
        receiver_phone: null,
        receiver_signature: null
      },
      searchQuery: '',
      selectedSearchItem: null,
      acOptions: {
        debounce: 500,
        placeholder: 'Courier Name or Email',
        inputClass: 'form-control'
      },
      minCharacter: 2,
      allowResetCourier: false,
      currentPicture: '',
      toAddress: '',
      incidenceDetails: {},
      cancelDetails: {},
      tobereturnedStatusReasons: [],
      tobereturnedDetails: {},
      statusStrings: {
        'DRAFT': this.$pgettext('statuses', 'Received by the system'),
        'READY': this.$pgettext('statuses', 'Ready to be scheduled'),
        'WAITING': this.$pgettext('statuses', 'Searching for Moover'),
        'CONFIRMED': this.$pgettext('statuses', 'Moover in transit to origin'),
        'PICKEDUP': this.$pgettext('statuses', 'Shipping picked up on origin'),
        'INTRANSIT': this.$pgettext('statuses', 'Moover in transit to destination'),
        'DELIVERED': this.$pgettext('statuses', 'Shipping delivered'),
        'INCIDENCE': this.$pgettext('statuses', 'On incidence'),
        'CANCELED': this.$pgettext('statuses', 'Canceled'),
        'TOBERETURNED': this.$pgettext('statuses', 'To be returned to sender'),
        'RETURNED': this.$pgettext('statuses', 'Returned to sender')
      },
      reScheduledAt: null,
      reScheduledAtHour: '00:00',
      timeSlots: Array(24 * 2 + 1).fill(0).map((_, i) => {
        return {
          text: ('0' + ~~(i / 2) + ':0' + 60 * (i / 2 % 1)).replace(/\d(\d\d)/g, '$1'),
          value: ('0' + ~~(i / 2) + ':0' + 60 * (i / 2 % 1)).replace(/\d(\d\d)/g, '$1')
        }
      }),
      interval: null,
      markers: [],
      fitted: false,
      editing: null,
      patchFieldTrigger: null,
      patchField: {},
      statusUsers: [],
      forensicsLog: [],
      auditLog: {},
      showOriginal: false,
      detailsShowing: false,
      shippingPlatformInformation: {},
      tag: '',
      tags: [],
      autocompleteItems: [],
      debounce: null,
      messageThreadType: '',
      showTracking: false,
      heathPath: [],
      showHeathPath: false,
      trackingWorking: false,
      fetching: false,
      pickedupShippings: [],
      pickedupShippingsFields: [
        'id',
        'company_id',
        'status',
        'size_id',
        'shipping_type_id'
      ],
      itemEdit: {},
      eventsHistoryFields: [
        {
          key: 'event',
          label: this.$gettext('Event')
        },
        {
          key: 'data',
          label: this.$gettext('Data')
        },
        {
          key: 'applied_by',
          label: this.$gettext('By')
        },
        {
          key: 'created_at',
          label: this.$gettext('At')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      eventsHistory: [],
      staticTrackingCoords: [],
      trackingCsv: [],
      outsourcing: false,
      checkpointsList: {}
    }
  },
  computed: {
    validateIncidenceDesc() {
      return this.newShippingStatusMeta !== null && this.newShippingStatusMeta.length > 10
    },
    isEditable() {
      return this.shipping.status !== 'DELIVERED' &&
        this.shipping.status !== 'CANCELED' &&
        this.shipping.status !== 'RETURNED'
    },
    is3pable() {
      return this.shipping.status === 'READY' ||
        this.shipping.status === 'READYATSTORE'
    },
    hasShippingPlatformInformation() {
      let platformId = this.shipping.platform_id
      let platform = this.platforms.find(x => x.id === platformId)

      return platform ? platform.slug === 'meli' || platform.slug === 'tienda-nube' || platform.slug === 'shopify' : false
    },
    isReversible() {
      return this.isSuperAdmin || this.isOperatorSupervisor ? true : this.moment.utc(this.shipping.status_history[0].created_at) > this.moment().subtract(48, 'hours').utc()
    },
    isDeliverable() {
      return this.isSuperAdmin ||
        (!this.shipping.active_settings ||
          this.shipping.active_settings.findIndex(x => x.name === 'UpdateDeliveredItems') === -1 ||
          (
            this.shipping.active_settings.findIndex(x => x.name === 'UpdateDeliveredItems') !== -1 &&
            (
              this.shipping.items.findIndex(x => x.serial_number === null) === -1 &&
              this.shipping.items.findIndex(x => x.serial_number.length !== x.quantity) === -1
            )
          )
        )
    },
    transactionsLink() {
      return '/transactions?qs=' + JSON.stringify({
        type: [],
        relationship: [],
        shippingId: ['shipping_id', 'in', [this.shipping.id]],
        company: [],
        user: [],
        country: [],
        createdAtFrom: [],
        createdAtTo: [],
        imputationDateFrom: []
      })
    },
    hasQr() {
      return (this.shipping.shipping_type_id === this.shippingTypes.find(x => x.name === 'pickup').id) || (this.shipping.external_label && this.shipping.external_label.code) || (this.shipping.external_label && this.shipping.external_label.id)
    },
    hasDeliverySecurityCode() {
      return this.isEditable && this.shipping.uses_security_code && this.shipping.uses_security_code === true
    },
    hasDeliveryAuthorizedReceiver() {
      return this.isEditable && this.shipping.uses_security_authorized && this.shipping.uses_security_authorized === true
    }
  },
  watch: {
    shipping(newValue, oldValue) {
      this.fetchStatusUsers()
      this.tags = this.shipping.tags.map(x => {
        x.text = x.name
        return x
      })

      if (this.shipping.items && this.shipping.items.length > 0) {
        this.shipping.itemsToShow = this.shipping.items.map(x => {
          if (x.item) {
            x.item.quantity = x.item.quantity || 1
            x.item.description = x.item.description || x.item.name || this.$gettext('No item description')
            x.item.serial_number = x.item.serial_number || null
          } else {
            x.quantity = x.quantity || 1
            x.description = x.description || x.name || this.$gettext('No item description')
            x.serial_number = x.serial_number || null
          }

          return {
            id: x.id || x.item.id || null,
            description: x.description || x.item.description || this.$gettext('No item description'),
            quantity: x.quantity || x.item.quantity || 1,
            serial_number: x.serial_number || (x.item && x.item.serial_number ? x.item.serial_number : null),
            original_serial_number: JSON.parse(JSON.stringify(x.serial_number || (x.item && x.item.serial_number ? x.item.serial_number : null)))
          }
        })
      } else if (this.shipping.meta && this.shipping.meta.items && this.shipping.meta.items.length > 0) {
        this.shipping.itemsToShow = this.shipping.meta.items.map(x => {
          if (x.item) {
            x.item.quantity = x.item.quantity || 1
            x.item.description = x.item.description || x.item.name || this.$gettext('No item description')
            x.item.serial_number = x.item.serialNumbers || null
          } else {
            x.quantity = x.quantity || 1
            x.description = x.description || x.name || this.$gettext('No item description')
            x.serial_number = x.serialNumbers || null
          }

          return {
            id: x.id || x.item.id || null,
            description: x.description || x.item.description || this.$gettext('No item description'),
            quantity: x.quantity || x.item.quantity || 1,
            serial_number: x.serial_number || (x.item && x.item.serial_number ? x.item.serial_number : null),
            original_serial_number: JSON.parse(JSON.stringify(x.serial_number || (x.item && x.item.serial_number ? x.item.serial_number : null)))
          }
        })
      } else {
        this.shipping.itemsToShow = []
      }
      this.setCheckpointList()
    },
    tag: 'initTagItems',
    showHeathPath(val) {
      if (val) {
        this.$refs.fullMap.addHeathPath(this.heathPath)
      } else {
        this.$refs.fullMap.removeHeathPath()
      }
    },
    showTracking(val) {
      this.getShipping()
    },
    itemEdit(val) {
      if (!val.serial_number) {
        this.itemEdit.serial_number = []
        this.itemEdit.original_serial_number = []
      }
    }
  },
  mixins: [
    AddressMixins,
    ShippingsMixins
  ],
  created() {
    this.fetchVehicleTypes()
    this.fetchPlatforms()
    this.fetchShippingSizes()
    this.fetchShippingTypes()
    this.fetchReceiverTypes()
    this.fetchShippingStatusReasons()
  },
  methods: {
    setCheckpointList() {
      this.checkpointsList = {
        data: this.shipping.checkpoints.map((x, i) => {
          return {
            applied_by: x.user || this.$gettext('System'),
            change: (i + 1) + '. ' + (x.warehouse ? `<strong>${x.warehouse.reference}</strong> <small>[${this.$gettext('WH')}]</small><br>${x.place.formatted_address}` : x.place.formatted_address),
            created_at: x.created_at
          }
        }),
        pagination: {
          total: this.shipping.checkpoints.length,
          limit: this.shipping.checkpoints.length,
          page: 0
        }
      }
    },
    updatePatch(obj, prop, event) {
      this.patchField[prop] = event
    },
    patchShipping() {
      if (
        confirm(this.$gettext('Are you sure you want to MODIFY current shipping field?'))
      ) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id, this.patchField)
          .then(response => {
            this.$parent.getShipping()
            this.originalShipping = {}
            this.patchField = {}
            this.editing = null
            this.searchQuery = ''
          })
      }
    },
    initMap() {
      this.getShipping()
      this.interval = setInterval(function () {
        this.getShipping()
      }.bind(this), 30000)
    },
    killMap() {
      this.markers = []
      clearInterval(this.interval)
    },
    // Tags
    initTagItems() {
      if (this.tag.length < 2) return
      let params = {
        filter_groups: [
          {
            filters: [
              ['name', 'ct', this.tag]
            ]
          }
        ]
      }
      /*
       * ! laksdñlaskd
       */
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.$http.get(this.$appConfig.api.baseUrl + 'tags', {params: params})
          .then(response => {
            this.autocompleteItems = response.body.data.map(x => {
              x.text = x.name
              return x
            })
          }).catch(() => console.warn('Oh. Something went wrong'))
      }, 600)
    },
    checkTag(obj) {
      if (!obj.tag.id) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tags', {name: obj.tag.text.toLowerCase()})
          .then(response => {
            obj.tag.id = response.body.id
            obj.tag.text = response.body.name
            this.$http.post(this.$appConfig.api.baseUrl + 'tags-models', {tag_id: response.body.id, model_type: 'shipping', model_id: this.shipping.id + ''})
              .then(response => {
                obj.addTag()
              })
          })
      } else {
        this.$http.post(this.$appConfig.api.baseUrl + 'tags-models', {tag_id: obj.tag.id, model_type: 'shipping', model_id: this.shipping.id + ''})
          .then(response => {
            obj.addTag()
          })
      }
    },
    deleteTag(obj) {
      this.$http.delete(this.$appConfig.api.baseUrl + 'tags-models/' + obj.tag.tag_model_id)
        .then(response => {
          obj.deleteTag()
        })
    },
    refreshShipping() {
      this.$emit('shippingupdated', this.shipping)
    },
    getShipping() {
      this.updating = true
      this.$http.get(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id, {params: {includes: ['courier.courier_available.tracking', 'courier_company']}})
        .then(response => {
          let noCourierYet = this.markers.length === 2 || false

          this.markers = []
          this.markers.push({
            id: 10000,
            lat: response.body.address_from.lat,
            lng: response.body.address_from.lng,
            icon: this.$appConfig.staticBaseUrl + 'static/img/ic_origin_on.svg',
            x: 12,
            y: 12,
            infoText: this.$gettext('Origin'),
            tooltip: true
          })
          this.markers.push({
            id: 10001,
            lat: response.body.address_to.lat,
            lng: response.body.address_to.lng,
            icon: this.$appConfig.staticBaseUrl + 'static/img/ic_destination_on.svg',
            x: 12,
            y: 12,
            infoText: this.$gettext('Destination'),
            tooltip: true
          })

          if (response.body.current_tracking !== null) {
            let icon = this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === response.body.courier.courier_available.vehicle_type_id).name.toLowerCase() + '-gray.svg'
            if (noCourierYet) {
              this.fitted = false
            }
            this.markers.push({
              id: 1000,
              lat: response.body.current_tracking.lat,
              lng: response.body.current_tracking.lng,
              icon: icon,
              x: 25,
              y: 25,
              infoText: this.$gettext('Courier'),
              tooltip: true
            })
          }
          this.getTracking()
            .then(() => {
              this.$refs.fullMap.addMarkers(this.markers)
              if (this.showTracking && this.shipping.courier_id) {
                let polyLines = [{
                  polyLine: this.markers.filter(x => x.id > 1000 && x.id < 10000),
                  polyLineOptions: {strokeColor: '#000000'}
                }]
                this.$refs.fullMap.addPolylines(polyLines)
              }
              if (this.fitted === false) {
                this.$refs.fullMap.fitted = false
                this.fitted = true
              }
            })
        })
    },
    getPickedupShippings() {
      this.fetching = true
      this.doGetPickedupShippings()
    },
    doGetPickedupShippings(previousShippings = [], page = 0) {
      let params = {
        limit: 200,
        page: page,
        includes: [
          'company',
          'status_history'
        ],
        filter_groups: [
          {
            filters: [
              ['pickup_shipping_id', 'eq', this.shipping.id]
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'shippings', {params})
        .then(response => {
          if (response.body.data.length > 0) {
            page++
            this.doGetPickedupShippings(previousShippings.concat(response.body.data), page)
          } else {
            this.pickedupShippings = previousShippings.map(x => {
              x.status_history.reverse()
              return x
            })
            this.fetching = false
          }
        })
    },
    getFirstIntransitDate(history) {
      const createdAt = history.find(x => x.status === 'INTRANSIT').created_at
      return this.moment.utc(createdAt).tz(this.$tz).format('L') + '\n' + this.moment.utc(createdAt).tz(this.$tz).format('LT')
    },
    printManifest() {
      this.$htmlToPaper('pickedup-manifest')
    },
    fetchForensicsLog(page = 0) {
      let params = {
        limit: 500,
        page: page,
        includes: [
          'applied_by'
        ],
        sort: [{
          key: 'created_at',
          direction: 'desc'
        }],
        filter_groups: [{
          filters: [
            ['shipping_id', 'eq', this.shipping.id]
          ]
        }]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'shippings/history', {params})
        .then(response => {
          response.body.data = response.body.data.map((x, i) => {
            x.change = this.isJson(x.details)
            if (i === response.body.data.length - 1) {
              x.hiddenData = true
              x.showOriginal = false
            }
            return x
          })
          this.forensicsLog = response.body
          this.$refs.modalForensicsLog.show()
        })
    },
    fetchAuditLog(page = 1) {
      let params = {
        limit: 25,
        page: page
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'shippings/' + this.$route.params.id + '/audit', { params })
        .then(response => {
          response.body.data = response.body.data.map(x => {
            const targetKeys = Object.keys(x).filter((x) => /target_/.test(x))
            let targets = []
            for (const key in x) {
              if (Object.hasOwnProperty.call(x, key) && targetKeys.indexOf(key) !== -1 && key.indexOf('_accountId') === -1 && key.indexOf('_appliedBy') === -1 && key.indexOf('_priorData') === -1) {
                targets.push(this.isJson({ [key]: x[key] }))
              }
            }
            x = {
              change: ('<strong>' + this.camelCaseToTitleCase(x.event) + '</strong>' + (targets.length > 0 ? targets : '')).replace(/<\/ul>,<ul>/g, '').replace(/target_/g, ''),
              applied_by: {
                name: x.src_user_name
              },
              created_at: x.created_at
            }
            return x
          })
          this.auditLog = response.body
        })
    },
    fetchShippingPlatformInformation() {
      this.$http.get(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id + '/platform-information')
        .then(response => {
          this.shippingPlatformInformation = response.body
          this.$refs.modalShippingPlatformInformation.show()
        })
    },
    populateActions() {
      setTimeout(function () {
        console.log(Object.keys(this.shipping).length !== 0 && this.shipping.constructor === Object)
        if (Object.keys(this.shipping).length !== 0 && this.shipping.constructor === Object) {
          this.$refs.AdminShippingActions.shipping = this.shipping
          this.$refs.AdminShippingActions.courier = this.courier
          this.$refs.AdminShippingActions.allowResetCourier = this.allowResetCourier
        } else {
          this.populateActions()
        }
      }.bind(this), 500)
    },
    assignShipping(id) {
      console.log(id)
      console.log(this.courier)
      let params = {
        shipping_id: id,
        user_id: this.courier.id,
        status: 'CONFIRMED'
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'status', params)
        .then(response => {
          // TODO: emit shipping updated
          this.$emit('shippingupdated', this.shipping)
          // this.courierSelected(this.courier.id)
          this.newShippingStatus = null
          this.newShippingStatusMeta = null
        })
    },
    updateStatus(id) {
      // console.log(this.courier.id ?? this.$store.getters.profile.id)
      let params
      this.reScheduledAt = this.reScheduledAt && this.reScheduledAt.split('-')[0] >= 1000 ? this.reScheduledAt : null
      if ((this.newShippingStatus === 'READY' || this.newShippingStatus === 'WAITING') && this.reScheduledAt) {
        this.reScheduledAt += ' ' + this.reScheduledAtHour + ':00'
        let utcDate = this.moment(this.reScheduledAt).utc().format('YYYY-MM-DD HH:mm:ss')

        params = {
          shipping_id: id,
          user_id: this.$store.getters.profile.id,
          status: this.newShippingStatus,
          meta: 'RESCHEDULED AT ' + utcDate + ' UTC'
        }

        this.$http.post(this.$appConfig.api.baseUrl + 'status', params)
          .then(response => {
            let shipping = {
              scheduled_date: utcDate,
              instructions: 'RESCHEDULED ' + this.shipping.instructions
            }
            this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id, shipping)
              .then(response => {
                this.$emit('shippingupdated', this.shipping)
                this.reScheduledAt = null
                this.newShippingStatus = null
                this.newShippingStatusMeta = null
                this.incidenceDetails = {}
              })
          })
      } else {
        params = {
          shipping_id: id,
          user_id: this.$store.getters.profile.id,
          status: this.newShippingStatus,
          meta: this.newShippingStatusMeta || null
        }
        if (this.newShippingStatus === 'INCIDENCE') {
          params.details = this.incidenceDetails
        }
        if (this.newShippingStatus === 'TOBERETURNED') {
          params.details = this.tobereturnedDetails
        }
        if (this.newShippingStatus === 'DELIVERED') {
          params.details = this.deliveredStatusDetailFields
        }
        if (this.newShippingStatus === 'CANCELED') {
          params.details = this.cancelDetails
        }
        if (params.details && params.details.reason_id) {
          params.shipping_status_reasons_id = params.details.reason_id
        }
        this.$http.post(this.$appConfig.api.baseUrl + 'status', params)
          .then(response => {
            if (this.newShippingStatus === 'PICKEDUP') {
              this.newShippingStatus = 'INTRANSIT'
              setTimeout(function () {
                this.updateStatus(this.shipping.id)
              }.bind(this), 1000)
            } else {
              // TODO: emit shipping updated
              this.$emit('shippingupdated', this.shipping)
              // this.courierSelected(this.courier.id)
              this.newShippingStatus = null
              this.newShippingStatusMeta = null
              this.incidenceDetails = {}
            }
          })
      }
    },
    patchItem() {
      if (
        confirm(this.$gettext('Are you sure you want to EDIT the serial numbers of this shipping?\n\nWARNING: THIS ACTION CANNOT BE UNDONE!'))
      ) {
        let payload = {
          serial_number: this.itemEdit.serial_number.filter(Boolean)
        }
        payload.serial_number = payload.serial_number.length > 0 ? payload.serial_number : null
        this.$http.patch(this.$appConfig.api.baseUrl + 'shipping-items/' + this.itemEdit.id, payload)
          .then(response => {
            this.$emit('shippingupdated', this.shipping)
            this.itemEdit = {}
            this.editing = null
            this.closeModal('modalItemEdit')
          })
      }
    },
    clearSecurityOnDelivery(type) {
      if (
        confirm(this.$gettextInterpolate(this.$gettext('Are you sure you want to CLEAR the security %{type} on this shipping?\n\nWARNING: THIS ACTION CANNOT BE UNDONE!'), {type: type}))
      ) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id + '/delivery-' + type + '/clear')
          .then(response => {
            this.$emit('shippingupdated', this.shipping)
          })
      }
    },
    anonymizeShipping() {
      if (
        confirm(this.$gettext('Are you sure you want to ANONYMIZE the receiver data of this shipping?\n\nWARNING: THIS ACTION CANNOT BE UNDONE!'))
      ) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id + '/anonymize')
          .then(response => {
            this.$emit('shippingupdated', this.shipping)
          })
      }
    },
    onSearchCourierSelected(item) {
      this.courier = item
      this.$refs.courierAc.searchQuery = ''
    },
    resetData: function () {
      Object.assign(this.$data, this.$options.data.call(this))
    },
    closeModal(modalRef) {
      this.$refs[modalRef].hide()
    },
    addressFixed(modalRef) {
      this.$emit('shippingupdated', this.shipping)
      this.closeModal(modalRef)
    },
    async checkToPlace(place) {
      await this.checkPlace(place)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.toAddress = this.theCheckedAddress.formatted_address
          this.shipping.to_place_id = this.theCheckedAddress.id
          this.updatePatch(this.patchField, 'to_place_id', this.theCheckedAddress.id)
          this.validTo = true
          this.theCheckedAddress = {}
        } else if (this.theCheckedAddress.validAddress === false) {
          this.$refs.modalToPickPlace.show()
          setTimeout(function () {
            this.$refs.pickToPlaceMap.center = {
              lat: this.theCheckedAddress.lat,
              lng: this.theCheckedAddress.lng
            }
            this.$refs.pickToPlaceMap.reference = this.$refs.toAddress ? this.$refs.toAddress.searchQuery : this.$refs.acToPlaceId.searchQuery
            this.$refs.pickToPlaceMap.styles = 'height: 500px;'
          }.bind(this), 100)
        }
      })
    },
    setToPlaceId(placeId) {
      this.shipping.to_place_id = placeId
      this.updatePatch(this.patchField, 'to_place_id', placeId)
      this.validTo = true
    },
    setPlacePickedTo(place) {
      this.toAddress = place.formatted_address
      this.setToPlaceId(place.id)
      this.shipping.to_meta = place.reference ? place.reference + ' - ' + (this.shipping.to_meta || '') : this.shipping.to_meta
      this.$refs.modalToPickPlace.hide()
    },
    closeModalToPickPlace() {
      this.$refs.modalToPickPlace.hide()
    },
    async handleAddressTo() {
      if (!this.validTo && this.shipping.fields_revision_data && typeof this.shipping.fields_revision_data.to !== 'string') {
        let placeToCheck = this.shipping.to_place_id
        await this.checkToPlace(placeToCheck)
      }
      this.$nextTick(() => {
        if (this.shipping.to_place_id && this.validTo) {
          this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id, {to_place_id: this.shipping.to_place_id, to_meta: this.shipping.to_meta, to_extra: this.shipping.to_extra})
            .then(response => {
              this.$emit('shippingupdated', this.shipping)
              this.$refs.modalFixAddressTo.hide()
              this.theCheckedAddress = {}
              this.shipping = {}
              this.validTo = false
              this.refreshTable(this.filters)
            })
        }
      })
    },
    async checkFromPlace(place) {
      await this.checkPlace(place)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.fromAddress = this.theCheckedAddress.formatted_address
          this.shipping.from_place_id = this.theCheckedAddress.id
          this.updatePatch(this.patchField, 'from_place_id', this.theCheckedAddress.id)
          this.validFrom = true
          this.theCheckedAddress = {}
        } else if (this.theCheckedAddress.validAddress === false) {
          this.$refs.modalFromPickPlace.show()
          setTimeout(function () {
            this.$refs.pickFromPlaceMap.center = {
              lat: this.theCheckedAddress.lat,
              lng: this.theCheckedAddress.lng
            }
            this.$refs.pickFromPlaceMap.reference = this.$refs.fromAddress ? this.$refs.fromAddress.searchQuery : this.$refs.acFromPlaceId.searchQuery
            this.$refs.pickFromPlaceMap.styles = 'height: 500px;'
          }.bind(this), 200)
        }
      })
    },
    setFromPlaceId(placeId) {
      this.shipping.from_place_id = placeId
      this.updatePatch(this.patchField, 'from_place_id', placeId)
      this.validFrom = true
    },
    setPlacePickedFrom(place) {
      this.fromAddress = place.formatted_address
      this.setFromPlaceId(place.id)
      this.shipping.from_meta = place.reference ? place.reference + ' - ' + (this.shipping.from_meta || '') : this.shipping.from_meta
      this.$refs.modalFromPickPlace.hide()
    },
    closeModalFromPickPlace() {
      this.$refs.modalFromPickPlace.hide()
    },
    async handleAddressFrom() {
      if (!this.validFrom && this.shipping.fields_revision_data && typeof this.shipping.fields_revision_data.from !== 'string') {
        let placeToCheck = this.shipping.from_place_id
        await this.checkFromPlace(placeToCheck)
      }
      this.$nextTick(() => {
        if (this.shipping.from_place_id && this.validFrom) {
          this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id, {from_place_id: this.shipping.from_place_id, from_meta: this.shipping.from_meta, from_extra: this.shipping.from_extra})
            .then(response => {
              this.$emit('shippingupdated', this.shipping)
              this.$refs.modalFixAddressFrom.hide()
              this.theCheckedAddress = {}
              this.shipping = {}
              this.validFrom = false
              this.refreshTable(this.filters)
            })
        }
      })
    },
    async checkLastKnownPlace(place) {
      await this.checkPlace(place)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          this.lastKnownPlaceAddress = this.theCheckedAddress.formatted_address
          this.shipping.last_known_place_id = this.theCheckedAddress.id
          this.updatePatch(this.patchField, 'last_known_place_id', this.theCheckedAddress.id)
          this.validLastKnownPlace = true
          this.theCheckedAddress = {}
        } else if (this.theCheckedAddress.validAddress === false) {
          this.$refs.modalLastKnownPickPlace.show()
          setTimeout(function () {
            this.$refs.pickLastKnownPlaceMap.center = {
              lat: this.theCheckedAddress.lat,
              lng: this.theCheckedAddress.lng
            }
            this.$refs.pickLastKnownPlaceMap.reference = this.$refs.lastKnownAddress ? this.$refs.lastKnownAddress.searchQuery : this.$refs.acLastKnownPlaceId.searchQuery
            this.$refs.pickLastKnownPlaceMap.styles = 'height: 500px;'
          }.bind(this), 200)
        }
      })
    },
    setLastKnownPlaceId(placeId) {
      this.shipping.last_known_place_id = placeId
      this.updatePatch(this.patchField, 'last_known_place_id', placeId)
      this.validLastKnownPlace = true
    },
    setPlacePickedLastKnown(place) {
      this.lastKnownPlaceAddress = place.formatted_address
      this.$refs.acLastKnownPlaceId.searchQuery = place.formatted_address
      this.setLastKnownPlaceId(place.id)
      this.$refs.modalLastKnownPickPlace.hide()
    },
    closeModalLastKnownPickPlace() {
      this.$refs.modalLastKnownPickPlace.hide()
    },
    fetchStatusUsers() {
      let userIds = []
      for (const status of this.shipping.status_history) {
        if (status.user_id) {
          userIds.push(status.user_id)
        }
      }
      if (userIds.length > 0) {
        let params = {
          page: 0,
          limit: 100,
          filter_groups: [
            {
              filters: [['id', 'in', [...new Set(userIds)]]]
            }
          ]
        }
        this.$http.get(this.$appConfig.api.baseUrl + 'users', {params: params})
          .then(response => {
            this.statusUsers = response.body.data
          })
      }
    },
    recalculateBudget() {
      if (
        confirm(this.$gettext('Are you sure you want to RECALCULATE current shipping Budget?'))
      ) {
        this.$http.put(this.$appConfig.api.baseUrl + 'shippings/' + this.shipping.id + '/budget/recalculate')
          .then(response => {
            this.$parent.getShipping()
          })
      }
    },
    async getTracking() {
      if (this.showTracking && this.shipping.courier_id) {
        this.trackingWorking = true
        const params = {
          page: 0,
          limit: 1,
          date_from: this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at,
          date_to: this.shipping.status_history.findIndex(x => x.status === 'DELIVERED') !== -1 ? this.shipping.status_history.find(x => x.status === 'DELIVERED').created_at : this.moment.utc(this.shipping.status_history.find(x => x.status === 'CONFIRMED').created_at).add(24, 'hours').format('YYYY-MM-DD HH:mm:ss'),
          filter_groups: [
            {
              filters: [
                ['shipping_id', 'eq', this.shipping.id]
              ]
            }
          ]
        }

        return this.$http.get(this.$appConfig.api.baseUrl + 'tracking-history', {params: params})
          .then(response => {
            let markers = []
            let trackingCsv = []
            this.markers = this.markers.filter(x => x.id >= 10000)
            const uniqueTrackingPointsByGeo = response.body.data.filter((thing, index, self) =>
              index === self.findIndex((t) => {
                return thing.lat === t.lat && thing.lng === t.lng
              })
            )
            this.trackingWorking = false

            let timeThreshold = null
            uniqueTrackingPointsByGeo.forEach((item, i) => {
              if (timeThreshold === null || parseInt(item.created_at.replace(/\D/g, '')) > timeThreshold) {
                trackingCsv.push({
                  created_at_utc: item.created_at,
                  lat: item.lat,
                  lng: item.lng,
                  shipping_id: item.shipping_id,
                  user_id: item.user_id
                })
                markers.push({
                  id: 1001 + i,
                  lat: item.lat,
                  lng: item.lng,
                  icon: {
                    type: 'marker-circle',
                    size: [10, 10],
                    anchor: [5, 5],
                    color: '#000',
                    text: (i + 1)
                  },
                  x: 11,
                  y: 34,
                  infoText: (i + 1) + ': ' + this.moment.utc(item.created_at).tz(this.$tz).format('L LT'),
                  tooltip: true,
                  z: uniqueTrackingPointsByGeo[(i + 1)] ? this.getSpeed(item, uniqueTrackingPointsByGeo[(i + 1)]) : 0
                })
                timeThreshold = parseInt(item.created_at.replace(/\D/g, '')) + 10
              }
            })
            this.trackingCsv = trackingCsv
            this.markers = this.markers.concat(markers)

            this.heathPath = markers.map(x => {
              return [x.lat, x.lng, x.z]
            })
          })
      }
      return true
    },
    getEventsHistory() {
      const params = {
        includes: ['applied_by'],
        sort: [{
          key: 'created_at',
          direction: 'desc'
        }],
        filter_groups: [
          {
            filters: [
              ['shipping_id', 'eq', this.shipping.id]
            ]
          }
        ]
      }
      this.$http.get(this.$appConfig.api.baseUrl + 'shipping-event-histories', {params: params})
        .then(response => {
          this.eventsHistory = response.body.data
        })
    },
    downloadTrackingCsv() {
      let fileName = this.$gettextInterpolate(this.$gettext('Tracking history for shipping %{shippingId}'), {shippingId: this.shipping.id}) + '__' + this.moment().format('YYYYMMDDHHmmss')
      fileName = fileName.toLowerCase().replace(/\s/g, '-') + '.csv'
      this.download(this.toCsv(this.trackingCsv), 'text/csv', fileName)
    },
    async try3p() {
      if (confirm(this.$gettext('Are you sure you want to OUTSOURCE this shipping?'))) {
        this.outsourcing = true
        let result = Promise.all([this.check3pQuote(this.shipping)])
        result.then(async response => {
          for (const quote of response) {
            if (quote.quoteId) {
              await this.sendTo3p(quote)
            }
          }
          this.outsourcing = false
        }, e => {
          console.log(e)
        })
      }
    },
    tryCancel3p() {
      if (confirm(this.$gettext('Are you sure you want to CANCEL the OUTSOURCING of this shipping?'))) {
        Promise.all([this.cancel3p(this.shipping)])
      }
    }
  }
}
</script>

<style lang="scss">
.shipping-info, .current-courier {
  .btn {
    padding: 0.1rem 1rem;
  }
}
.shipping-item {
  .shipping-origin, .shipping-destination {
    .btn {
      padding: 0.1rem 1rem;
    }
  }
}
</style>
